import { CondoType, getBlockLabel, getUnitLabel } from './terminology';

interface RoleUnitInfo {
  number?: string;
  block?: { name?: string };
  street_name?: string;
  street_number?: string;
}

interface RoleUnitMetadata {
  unit_info?: RoleUnitInfo;
  unit_display?: string;
  unit?: string;
  unit_number?: string | number;
  block_name?: string;
  street_name?: string;
  street_number?: string;
  [key: string]: unknown;
}

export interface RoleUnitParts {
  /** The address line to lead with — the street when the condo has one. */
  primary: string;
  /** The internal reference (quadra/lote), or null when it is already the primary. */
  secondary: string | null;
}

/**
 * The unit a role points at, split into the two lines a detail panel shows.
 *
 * `unit_display` is a string frozen into role_metadata when the role was created, and some
 * creation paths (notably the resident's own "liberar visita" flow) build it from block +
 * number alone — so a lot on "Rua das Acácias, 100" reads as a bare "18 - 5", which is the
 * same text as every other lot 5 in the condo. The structured fields travel with the unit
 * and are refreshed on every read, so they are preferred; the frozen string is only a last
 * resort for roles that carry no unit reference (a free-text destination, for instance).
 *
 * The quadra is never dropped the way formatUnitLabel drops it — a porteiro directing
 * someone needs both the street and the quadra — it just moves to the second line.
 */
export function resolveRoleUnitParts(
  metadata: RoleUnitMetadata | undefined | null,
  condoType?: CondoType,
): RoleUnitParts | null {
  if (!metadata) return null;

  const info = metadata.unit_info;
  const streetName = info?.street_name || metadata.street_name;
  const streetNumber = info?.street_number || metadata.street_number;
  const blockName = info?.block?.name || metadata.block_name;
  const number = info?.number ?? (metadata.unit_number != null ? String(metadata.unit_number) : undefined);

  const street = streetName ? (streetNumber ? `${streetName}, ${streetNumber}` : streetName) : null;

  const internal: string[] = [];
  if (blockName) internal.push(`${getBlockLabel(condoType)} ${blockName}`);
  if (number) internal.push(`${getUnitLabel(condoType)} ${number}`);
  const internalLine = internal.length ? internal.join(' – ') : null;

  if (street) return { primary: street, secondary: internalLine };
  if (internalLine) return { primary: internalLine, secondary: null };

  const stored = metadata.unit_display || metadata.unit;
  return stored ? { primary: stored, secondary: null } : null;
}

/**
 * The same address on one line, for headings and other tight spots. Reads outside-in —
 * quadra, then street, then lote — rather than the two-line order.
 */
export function formatRoleUnitDisplay(
  metadata: RoleUnitMetadata | undefined | null,
  condoType?: CondoType,
): string | null {
  if (!metadata) return null;

  const info = metadata.unit_info;
  const streetName = info?.street_name || metadata.street_name;
  const streetNumber = info?.street_number || metadata.street_number;
  const blockName = info?.block?.name || metadata.block_name;
  const number = info?.number ?? (metadata.unit_number != null ? String(metadata.unit_number) : undefined);

  const parts: string[] = [];
  if (blockName) parts.push(`${getBlockLabel(condoType)} ${blockName}`);
  if (streetName) parts.push(streetNumber ? `${streetName}, ${streetNumber}` : streetName);
  if (number) parts.push(`${getUnitLabel(condoType)} ${number}`);
  if (parts.length) return parts.join(' – ');

  return metadata.unit_display || metadata.unit || null;
}
